/**
 * FixWindowHost — opens the "Fix issues" steps in their own window while
 * mounted, and closes it when unmounted.
 *
 * It opens over the task pane; Expand moves it into an Office dialog
 * (``review.html?window=fix``) over Word. Expand and Minimise switch
 * between the two and keep the writer's place. Where dialogs are unavailable (an old Word build, the browser preview,
 * or a blocked dialog) the same ``FixWorkspace`` renders over the task pane.
 * Either way the pane runs the fix pass and the window stays open to show it:
 * each issue's status as it goes, then the changes to review. With ``review``
 * the changes open in the same window (Approve / Reject each one), and the
 * window closes once none is left.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getRunProposals, type ResolveIssueDoc, type SectionProposal } from "../../api/client";
import type { ResolveSelectionTarget } from "../components/SubmissionIssuesTab";
import {
  filterReviewFlow,
  isNoopProposal,
  type UseProposalReviewFlowResult,
  useProposalReviewFlow,
} from "../hooks/useProposalReviewFlow";
import { fonts, inkColors, surfaceColors } from "../tokens";
import {
  EMPTY_FIX_DRAFT,
  type FixDocument,
  type FixDraft,
  type FixParentToWindow,
  type FixProgress,
  type FixReviewAction,
  type FixSetup,
  type FixWindowToParent,
  withDecisions,
} from "./fixProtocol";
import { FixWorkspace } from "./FixWorkspace";
import { parseMessage } from "./reviewProtocol";
import { ReviewWorkspace } from "./ReviewWorkspace";
import { dialogsSupported, type UseReviewWindowResult, useReviewWindow, WINDOW_SIZE } from "./useReviewWindow";

/** What the window needs to review the pass's changes itself. */
export interface FixReviewSource {
  runId: string;
  libraryId?: string | null;
  /** Bumped when the pass proposes a change, so the queue picks it up. */
  refreshSignal?: number;
  writeTitleFields?: (fields: Record<string, string>) => Promise<unknown>;
  /** A change was approved or rejected. */
  onDecided?: () => void;
}

export interface FixWindowHostProps {
  targets: ResolveSelectionTarget[];
  documents: FixDocument[];
  busy?: boolean;
  onCancel: () => void;
  onStart: (documentScope: ResolveIssueDoc[], targets: ResolveSelectionTarget[]) => void;
  /** The pass once started (null before Start). */
  progress?: FixProgress | null;
  onPause?: () => void;
  onResume?: () => void;
  onStop?: () => void;
  /** Review the changes in this window. Without it, ``onReview`` opens them
   *  elsewhere and the shell closes this window. */
  review?: FixReviewSource | null;
  /** Open the proposed changes (only without ``review``). */
  onReview?: () => void;
  /** Every change in the review was approved or rejected; the window closes next. */
  onReviewed?: () => void;
  /** Close the window after Start (the pass carries on). */
  onClose?: () => void;
  /** The shell's one review queue. Given, the window reviews its pass's changes
   *  from it (a filtered view) instead of loading a queue of its own. */
  flow?: UseProposalReviewFlowResult;
}

const useStyles = makeStyles({
  overlay: {
    position: "absolute",
    inset: 0,
    zIndex: 40,
    display: "flex",
    flexDirection: "column",
    backgroundColor: surfaceColors.paper,
  },
  waiting: {
    position: "absolute",
    inset: 0,
    zIndex: 40,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "24px",
    textAlign: "center",
    backgroundColor: surfaceColors.paper,
    fontFamily: fonts.body,
    fontSize: "13px",
    color: inkColors.ink3,
  },
  cancel: {
    height: "30px",
    padding: "0 14px",
    borderRadius: "6px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    backgroundColor: surfaceColors.paper,
    fontFamily: fonts.body,
    fontSize: "13px",
    cursor: "pointer",
  },
});

export function fixWindowUrl(loc: Pick<Location, "origin" | "pathname" | "search"> = window.location): string {
  // The pane's own page, as for the review window (``reviewWindowUrl``).
  const params = new URLSearchParams(loc.search);
  params.set("window", "fix");
  return `${loc.origin}${loc.pathname}?${params.toString()}`;
}

/** The selection as the window shows it. Targets without issue details (an
 *  older caller) show each flag by id so nothing is silently dropped. */
export function toFixSetup(targets: ResolveSelectionTarget[], documents: FixDocument[]): FixSetup {
  return {
    sections: targets.map((t) => ({
      module_id: t.module_id,
      number: t.number,
      title: t.title,
      issues: t.issue_ids.map((id) => t.issues?.find((i) => i.id === id) ?? { id, label: "Flag", description: "" }),
    })),
    documents,
  };
}

/** Keep only the issues the writer left ticked, section by section. */
export function keepIssues(targets: ResolveSelectionTarget[], issueIds: string[]): ResolveSelectionTarget[] {
  const keep = new Set(issueIds);
  return targets
    .map((t) => ({
      ...t,
      issue_ids: t.issue_ids.filter((id) => keep.has(id)),
      issues: t.issues?.filter((i) => keep.has(i.id)),
    }))
    .filter((t) => t.issue_ids.length > 0);
}

export const FixWindowHost: React.FC<FixWindowHostProps> = ({
  targets,
  documents,
  busy,
  onCancel,
  onStart,
  progress: passProgress = null,
  onPause,
  onResume,
  onStop,
  review = null,
  onReview,
  onReviewed,
  onClose,
  flow: sharedFlow,
}) => {
  const styles = useStyles();
  const setup = useMemo(() => toFixSetup(targets, documents), [targets, documents]);
  // A change approved or rejected after the pass (here, in the Issues tab, or
  // in the shell's review) no longer counts as ready: the run's proposals,
  // re-read on every decision, turn its "Change ready" into Approved / Rejected.
  const [decided, setDecided] = useState<SectionProposal[]>([]);
  const passEnded = Boolean(passProgress && (passProgress.finished || passProgress.paused));
  const runId = review?.runId ?? null;
  const decisionSignal = review?.refreshSignal;
  // biome-ignore lint/correctness/useExhaustiveDependencies: decisionSignal is the re-read trigger
  useEffect(() => {
    if (!runId || !passEnded) return;
    let live = true;
    getRunProposals(runId, "all")
      .then((resp) => {
        if (!live) return;
        setDecided(
          resp.sections.flatMap((s) =>
            s.proposals.filter((p) => p.status !== "superseded" && !(p.status === "pending" && isNoopProposal(p))),
          ),
        );
      })
      .catch(() => {
        // Keep the pass's own statuses; the next decision reads again.
      });
    return () => {
      live = false;
    };
  }, [runId, passEnded, decisionSignal]);
  const progress = useMemo<FixProgress | null>(
    () =>
      passProgress && passEnded && decided.length > 0
        ? { ...passProgress, status: withDecisions(passProgress.status, decided) }
        : passProgress,
    [passProgress, passEnded, decided],
  );
  // The review step: the pass's own changes, one at a time, in this window.
  const [reviewing, setReviewing] = useState(false);
  const passIds = useMemo(() => new Set(progress ? Object.keys(progress.status) : []), [progress]);
  const moduleIds = useMemo(() => new Set(targets.map((t) => t.module_id)), [targets]);
  const include = useCallback(
    (p: SectionProposal) =>
      p.issue_ids.length > 0 ? p.issue_ids.some((id) => passIds.has(id)) : moduleIds.has(p.module_id),
    [passIds, moduleIds],
  );
  const ownFlow = useProposalReviewFlow({
    // Fetched only once the writer opens the review — never with a shared queue.
    runId: !sharedFlow && reviewing && review ? review.runId : null,
    refreshSignal: review?.refreshSignal,
    libraryId: review?.libraryId,
    onDecided: review?.onDecided,
    writeTitleFields: review?.writeTitleFields,
    include,
  });
  const flow = useMemo(
    () => (sharedFlow ? filterReviewFlow(sharedFlow, include) : ownFlow),
    [sharedFlow, include, ownFlow],
  );
  // A shared queue's active card may be outside this pass: bring the first of
  // the pass's changes forward so the window opens on one of them.
  const firstInView = flow.queue[0]?.proposal.id ?? null;
  const sharedSelect = sharedFlow?.selectById;
  useEffect(() => {
    if (reviewing && sharedSelect && !flow.active && firstInView) sharedSelect(firstInView);
  }, [reviewing, sharedSelect, flow.active, firstInView]);
  // Its decisions (approve / reject a change that is not the active one, Approve
  // all); this host shows the view, so the hook's own window stays closed.
  const reviewWindow = useReviewWindow(flow);
  const reviewState = reviewing ? reviewWindow.state : null;
  // Opens over the pane; Expand moves it into a window over the whole of Word.
  const [mode, setMode] = useState<"opening" | "dialog" | "overlay">("overlay");
  const dialogRef = useRef<Office.Dialog | null>(null);
  const askedRef = useRef(false);
  const draftRef = useRef<FixDraft>(EMPTY_FIX_DRAFT);
  const latest = useRef({
    setup,
    targets,
    onCancel,
    onStart,
    progress,
    onPause,
    onResume,
    onStop,
    review,
    onReview,
    onClose,
    reviewWindow,
    reviewState,
  });
  latest.current = {
    setup,
    targets,
    onCancel,
    onStart,
    progress,
    onPause,
    onResume,
    onStop,
    review,
    onReview,
    onClose,
    reviewWindow,
    reviewState,
  };
  // Closing after Start leaves the pass running; before Start it cancels.
  const closeOrCancel = () =>
    (latest.current.progress ? (latest.current.onClose ?? latest.current.onCancel) : latest.current.onCancel)();
  const closeRef = useRef(closeOrCancel);
  closeRef.current = closeOrCancel;
  /** "Review N changes": here when the host can, else the shell's own view.
   *  Returns false when the shell takes it (and closes this window). */
  const openReview = (): boolean => {
    if (!latest.current.review) {
      latest.current.onReview?.();
      return false;
    }
    setReviewing(true);
    return true;
  };
  const openReviewRef = useRef(openReview);
  openReviewRef.current = openReview;

  // Every change decided: the review is done, so the window closes. Not when
  // the last decision left an error (it shows on the empty view instead), nor
  // when the review opened empty ("Nothing left to review" + Close).
  const hadChangesRef = useRef(false);
  const onReviewedRef = useRef(onReviewed);
  onReviewedRef.current = onReviewed;
  useEffect(() => {
    if (!reviewing) {
      hadChangesRef.current = false;
      return;
    }
    if (flow.queue.length > 0) hadChangesRef.current = true;
    // Not "idle": a shared queue can still hold changes outside this pass.
    else if (hadChangesRef.current && flow.phase !== "applying" && flow.phase !== "revising" && !flow.error) {
      hadChangesRef.current = false;
      onReviewedRef.current?.();
      closeRef.current();
    }
  }, [reviewing, flow.queue.length, flow.phase, flow.error]);

  useEffect(() => {
    // Once per open: React's dev double-run must not ask Word for a second window.
    if (mode !== "opening" || askedRef.current) return;
    askedRef.current = true;
    let closedByUs = false;
    Office.context.ui.displayDialogAsync(fixWindowUrl(), { ...WINDOW_SIZE, displayInIframe: true }, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        setMode("overlay");
        return;
      }
      const dialog = result.value;
      dialogRef.current = dialog;
      const done = () => {
        closedByUs = true;
        dialogRef.current = null;
        try {
          dialog.close();
        } catch {
          // already closed
        }
      };
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
        if (!("message" in arg)) return;
        const msg = parseMessage<FixWindowToParent>(arg.message);
        if (!msg) return;
        if (msg.type === "ready") {
          const out: FixParentToWindow = {
            type: "setup",
            setup: latest.current.setup,
            draft: draftRef.current,
            progress: latest.current.progress,
            review: latest.current.reviewState,
          };
          sentReviewRef.current = JSON.stringify(latest.current.reviewState);
          dialog.messageChild(JSON.stringify(out));
        } else if (msg.type === "draft") {
          draftRef.current = msg.draft;
        } else if (msg.type === "minimise") {
          done();
          setMode("overlay");
        } else if (msg.type === "start") {
          // The window stays open: it shows the pass from here on.
          latest.current.onStart(msg.documentScope, keepIssues(latest.current.targets, msg.issueIds));
        } else if (msg.type === "pause") {
          latest.current.onPause?.();
        } else if (msg.type === "resume") {
          latest.current.onResume?.();
        } else if (msg.type === "stop") {
          latest.current.onStop?.();
        } else if (msg.type === "review") {
          // Reviewed in this window when the host can; else the shell's view.
          if (!openReviewRef.current()) done();
        } else if (msg.type === "reviewAction") {
          runReviewAction(latest.current.reviewWindow, msg.action);
        } else if (msg.type === "cancel" || msg.type === "close") {
          done();
          closeRef.current();
        }
      });
      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
        // Closed with the window's own X.
        dialogRef.current = null;
        if (!closedByUs) closeRef.current();
      });
      setMode("dialog");
    });
  }, [mode]);

  // The review queue goes to the open window as it moves (only when it changed:
  // the state is rebuilt on every render).
  const sentReviewRef = useRef("null");
  useEffect(() => {
    if (!dialogRef.current) return;
    const key = JSON.stringify(reviewState);
    if (key === sentReviewRef.current) return;
    sentReviewRef.current = key;
    const out: FixParentToWindow = { type: "review", review: reviewState };
    try {
      dialogRef.current.messageChild(JSON.stringify(out));
    } catch {
      // The window closed between the change and the send.
    }
  });

  // Every change to the pass goes to the open window.
  useEffect(() => {
    if (!progress || !dialogRef.current) return;
    const out: FixParentToWindow = { type: "progress", progress };
    try {
      dialogRef.current.messageChild(JSON.stringify(out));
    } catch {
      // The window closed between the change and the send.
    }
  }, [progress]);

  // Unmounted by the shell (cancelled from the pane, or the run changed).
  useEffect(
    () => () => {
      try {
        dialogRef.current?.close();
      } catch {
        // already closed
      }
      dialogRef.current = null;
    },
    [],
  );

  const expand = () => {
    askedRef.current = false;
    setMode("opening");
  };

  if (mode === "overlay" && reviewing) {
    return (
      // biome-ignore lint/a11y/useSemanticElements: a full-pane overlay, not a native modal
      <div className={styles.overlay} role="dialog" aria-label="Review changes">
        <ReviewWorkspace
          state={reviewWindow.state}
          onSelect={reviewWindow.onSelect}
          onApprove={reviewWindow.onApprove}
          onReject={reviewWindow.onReject}
          onRevise={reviewWindow.onRevise}
          onApproveAll={reviewWindow.onApproveAll}
          onStopAll={reviewWindow.onStopAll}
          onUndo={reviewWindow.onUndo}
          onClose={closeOrCancel}
          placement="pane"
          onResize={dialogsSupported() ? expand : undefined}
        />
      </div>
    );
  }

  if (mode === "overlay") {
    return (
      // biome-ignore lint/a11y/useSemanticElements: a full-pane overlay, not a native modal
      <div className={styles.overlay} role="dialog" aria-label="Fix issues">
        <FixWorkspace
          setup={setup}
          busy={busy}
          onCancel={onCancel}
          onStart={(ids, scope) => onStart(scope, keepIssues(targets, ids))}
          draft={draftRef.current}
          onDraftChange={(d) => {
            draftRef.current = d;
          }}
          placement="pane"
          onResize={dialogsSupported() ? expand : undefined}
          progress={progress}
          onPause={onPause}
          onResume={onResume}
          onStop={onStop}
          onReview={review || onReview ? () => void openReview() : undefined}
          onClose={onClose}
        />
      </div>
    );
  }

  return (
    <div className={styles.waiting} aria-live="polite">
      <span>
        {reviewing
          ? "The changes are open for review in their own window."
          : progress
            ? "The fix is running in its own window."
            : "Choose what to fix in the window that just opened."}
      </span>
      <button type="button" className={styles.cancel} onClick={progress ? (onClose ?? onCancel) : onCancel}>
        {progress ? "Close the window" : "Cancel"}
      </button>
    </div>
  );
};

/** A review click from the Fix window, applied through the review flow. */
function runReviewAction(h: UseReviewWindowResult, action: FixReviewAction): void {
  switch (action.type) {
    case "select":
      h.onSelect(action.id);
      return;
    case "approve":
      h.onApprove(action.id, action.content);
      return;
    case "reject":
      h.onReject(action.id);
      return;
    case "revise":
      h.onRevise(action.id, action.guidance);
      return;
    case "approveAll":
      h.onApproveAll();
      return;
    case "stopAll":
      h.onStopAll();
      return;
    case "undo":
      h.onUndo();
      return;
  }
}

export default FixWindowHost;
